import React from "react";
import { Button, CircularProgress } from "@mui/material";

/** Type/weight shared across every size. */
const BUTTON = {
  minWidth: 62,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.57
};

/**
 * Per-size geometry. `md` is the canonical reskin button (height 30, 5px radius
 * — e.g. the "Save" button); `lg` is the larger CTA (height 36, 8px radius —
 * e.g. "Change Password"). Named so future sizes are additive, mirroring
 * shadcn's size prop.
 */
const SIZE_MAP = {
  md: {
    height: 30,
    padding: "6px 12px",
    borderRadius: "5px"
  },
  lg: {
    height: 36,
    padding: "8px 16px",
    borderRadius: "8px"
  }
};

/** Inline spinner shown by `loading` buttons (18px matches the reskin). The
    filled spinner matches the filled button's content color (colors_v2 token). */
const SPINNER = {
  size: 18,
  filledColor: "var(--buttons-functional-content-default)"
};

/**
 * BaseButton — the canonical reskin button.
 *
 * WHAT IT IS: a thin wrapper over MUI `Button` that bakes in the repeated
 * reskin defaults (30px height, 62px min-width, 14/600 type, no text-transform,
 * no elevation) so call sites stop copy-pasting the same `sx` block.
 *
 * WHAT IT WRAPS: MUI `Button`. The `variant` prop is mapped from our semantic
 * names to MUI's: "filled" -> "contained", "text" -> "text".
 *
 * CONTRACT (props in):
 *  - variant: "filled" | "text" (default "filled")
 *  - size: "md" | "lg" (default "md"). md = height 30 / 5px radius (the
 *      canonical button, e.g. "Save"); lg = height 36 / 8px radius (larger CTA,
 *      e.g. "Change Password"). Named so future sizes are additive, mirroring
 *      shadcn's size prop.
 *  - loading: bool — renders an 18px spinner (white on filled), disables the
 *      button, and ignores children. Replaces the repeated
 *      `isLoading ? <CircularProgress/> : children` ternary.
 *  - loadingTestId: string — data-testid put on the spinner so existing
 *      "*-loading" selectors keep resolving after migration.
 *  - spinnerSize: number — override the 18px spinner (a few legacy buttons used
 *      14/28px; pass this to preserve that exact size).
 *  - disabled, onClick, type, children, aria-label, data-testid, className: all
 *      forwarded to MUI Button.
 *  - sx: merged OVER the variant defaults so per-call overrides (e.g. `gap`,
 *      a different spinner-less width) still win.
 *  - ref: forwarded to the underlying button element.
 *
 * BEHAVIOR (out): a styled, optionally-loading button. When `loading`, it is
 * disabled and shows only the spinner.
 *
 * data-testid: not derived — pass it explicitly; it is forwarded verbatim so
 * existing selectors (e.g. "continue-button-company-info") keep working.
 *
 * MIGRATION NOTE (shadcn): the public API (variant/size/loading/asChild-style
 * pass-through + ref forwarding) is intentionally shaped like shadcn's Button.
 * Swapping the implementation later is a body-only change — the `VARIANT_MAP`
 * and the MUI element get replaced with `cva(...)` + a Tailwind `<button>`,
 * while call sites keep passing the same semantic props.
 */

// Semantic variant -> (MUI variant, spinner color, color sx). Acts like a cva
// variant map so the shadcn port is a 1:1 translation. Colors come straight from
// the colors.css design tokens.
const VARIANT_MAP = {
  filled: {
    muiVariant: "contained",
    spinnerColor: SPINNER.filledColor,
    colorSx: {
      // !important so the token wins over MUI's own .MuiButton-contained
      // background/overlay; the interaction states below match it.
      backgroundColor: "var(--buttons-functional-default-background-default) !important",
      color: "var(--buttons-functional-content-default) !important",
      // Hover repaints to the colors_v2 hover token (replaces MUI's default
      // darkening overlay). Guard against disabled so a disabled button shows no
      // hover affordance. There is no pressed/active token in colors_v2, so the
      // pressed state intentionally reuses the hover background.
      "&:hover:not(.Mui-disabled)": {
        backgroundColor:
          "var(--buttons-functional-hover-background-default) !important"
      },
      // Disabled state lives on the base component so call sites don't each
      // redefine it. Uses the colors_v2 dysfunctional tokens.
      "&.Mui-disabled": {
        backgroundColor: "var(--buttons-dysfunctional-background-default) !important",
        color: "var(--buttons-dysfunctional-content-default) !important"
      }
    }
  },
  text: {
    muiVariant: "text",
    spinnerColor: "inherit",
    colorSx: {
      color: "var(--buttons-functional-content-skeleton) !important",
      // The text button has no fill at rest; on hover it picks up the skeleton
      // hover token (matches the filled button's hover treatment).
      "&:hover:not(.Mui-disabled)": {
        backgroundColor:
          "var(--buttons-functional-hover-background-skeleton) !important"
      },
      "&.Mui-disabled": {
        color: "var(--buttons-dysfunctional-content-default) !important"
      }
    }
  }
};

const baseSx = {
  minWidth: BUTTON.minWidth,
  fontSize: BUTTON.fontSize,
  fontWeight: BUTTON.fontWeight,
  lineHeight: BUTTON.lineHeight,
  textTransform: "none",
  // MUI disables a button with pointer-events:none, so the OS default arrow
  // shows on hover. Re-enable pointer events on the disabled state and pin the
  // not-allowed cursor so a disabled button reads as disabled.
  "&.Mui-disabled": {
    pointerEvents: "auto",
    cursor: "not-allowed"
  }
};

const BaseButton = React.forwardRef(
  (
    {
      variant = "filled",
      size = "md",
      loading = false,
      loadingTestId,
      spinnerSize = SPINNER.size,
      disabled = false,
      children,
      sx,
      ...props
    },
    ref
  ) => {
    const { muiVariant, spinnerColor, colorSx } =
      VARIANT_MAP[variant] || VARIANT_MAP.filled;
    const sizeSx = SIZE_MAP[size] || SIZE_MAP.md;

    return (
      <Button
        ref={ref}
        variant={muiVariant}
        color="primary"
        disableElevation
        disabled={disabled || loading}
        // Caller sx spreads last so per-call overrides win over the defaults.
        sx={{ ...baseSx, ...sizeSx, ...colorSx, ...sx }}
        {...props}
      >
        {loading ? (
          <CircularProgress
            size={spinnerSize}
            data-testid={loadingTestId}
            sx={{ color: spinnerColor }}
          />
        ) : (
          children
        )}
      </Button>
    );
  }
);

BaseButton.displayName = "BaseButton";

export default BaseButton;
